import { useState, useEffect } from "react";
import api from "../api/axios";

const SCENTS = [
  { value: "coffee",      label: "Coffee",         emoji: "☕" },
  { value: "roast_tea",   label: "Roast Tea",      emoji: "🍂" },
  { value: "green_tea",   label: "Green Tea",      emoji: "🍵" },
  { value: "rose",        label: "Rose",           emoji: "🌹" },
  { value: "jasmine",     label: "Jasmine",        emoji: "🌸" },
  { value: "sandalwood",  label: "Sandalwood",     emoji: "🪵" },
  { value: "vanilla",     label: "Vanilla",        emoji: "🤍" },
  { value: "custom",      label: "Other / Custom", emoji: "🎨" },
];

const SIZES = [
  { value: "small",      label: "Small",      sub: "50g · ₹70–₹130",    emoji: "🕯️" },
  { value: "medium",     label: "Medium",     sub: "150g · ₹500",        emoji: "🕯️🕯️" },
  { value: "large",      label: "Large",      sub: "400g · ₹1,500",      emoji: "🕯️🕯️🕯️" },
  { value: "extra",      label: "Extra Large", sub: "500g+ · ₹2,000",   emoji: "🕯️🕯️🕯️🕯️" },
];

const EMPTY = {
  full_name: "", email: "", phone: "",
  address: "",
  scent: "", size: "", color: "",
  quantity: 1, label_text: "", notes: "",
};

export default function CustomizationSection() {
  const [form, setForm]           = useState(EMPTY);
  const [errors, setErrors]       = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess]     = useState(false);
  const [apiError, setApiError]   = useState("");
  const [refImage, setRefImage]   = useState(null);
  const [refPreview, setRefPreview] = useState(null);

  // Auto-fill from logged-in user profile
  useEffect(() => {
    api.get("users/profile/")
      .then(res => {
        const user = res.data;
        if (!user) return;
        setForm(f => ({
          ...f,
          full_name: user.first_name ? `${user.first_name} ${user.last_name || ""}`.trim() : (user.username || ""),
          email:     user.email || "",
          phone:     user.contact_number || "",
          address:   formatAddress(user.default_address || user.addresses?.[0]),
        }));
      })
      .catch(() => {});
  }, []);

  const formatAddress = (addr) => {
    if (!addr) return "";
    const parts = [addr.address_line_1];
    if (addr.address_line_2) parts.push(addr.address_line_2);
    if (addr.landmark)       parts.push(`Near ${addr.landmark}`);
    parts.push(`${addr.city}, ${addr.state} — ${addr.postal_code}`);
    parts.push(addr.country || "India");
    return parts.join(", ");
  };

  const set = (field) => (e) => {
    setForm(f => ({ ...f, [field]: e.target.value }));
    setErrors(er => ({ ...er, [field]: "" }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setRefImage(file);
    setRefPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async () => {
    setApiError("");
    setErrors({});

    const errs = {};
    if (!form.full_name.trim()) errs.full_name = "Name is required.";
    if (!form.email.trim())     errs.email     = "Email is required.";
    if (!form.phone.trim())     errs.phone     = "Phone number is required.";
    if (!form.scent)            errs.scent     = "Please choose a scent.";
    if (!form.size)             errs.size      = "Please choose a size.";
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries({ ...form, quantity: Number(form.quantity) || 1 })
        .forEach(([k, v]) => fd.append(k, v));
      if (refImage) fd.append("reference_image", refImage);

      await api.post("customizations/submit/", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setSuccess(true);
      setForm(EMPTY);
      setRefImage(null);
      setRefPreview(null);
    } catch {
      setApiError("Could not connect to server. Please try again later.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = (field) => ({
    width: "100%", padding: "11px 14px", borderRadius: "10px", boxSizing: "border-box",
    border: `1.5px solid ${errors[field] ? "#e57373" : "#4a3728"}`,
    background: "rgba(255,255,255,0.07)", fontFamily: "Georgia, serif",
    fontSize: "13px", outline: "none", color: "#fff",
  });

  const label = {
    display: "block", fontFamily: "Georgia, serif", fontSize: "11px",
    fontWeight: "700", color: "#ccc", textTransform: "uppercase",
    letterSpacing: "0.08em", marginBottom: "6px",
  };

  const errMsg = (field) => errors[field]
    ? <p style={{ fontFamily: "Georgia, serif", fontSize: "10px", color: "#e57373", marginTop: "4px" }}>⚠ {errors[field]}</p>
    : null;

  if (success) {
    return (
      <section style={{ background: "linear-gradient(160deg, #1a1a1a 0%, #2d1f0e 100%)", padding: "80px 6%" }}>
        <div style={{ maxWidth: "860px", margin: "0 auto", background: "rgba(58,138,104,0.15)",
          border: "1.5px solid #3a8a68", borderRadius: "18px", padding: "52px 32px", textAlign: "center" }}>
          <div style={{ fontSize: "56px", marginBottom: "18px" }}>🕯️</div>
          <h3 style={{ fontFamily: "Georgia, serif", fontSize: "22px", fontWeight: "700", color: "#fff", marginBottom: "10px" }}>
            Request Received!
          </h3>
          <p style={{ fontFamily: "Georgia, serif", fontSize: "14px", color: "#aaa", lineHeight: "1.8", maxWidth: "400px", margin: "0 auto 28px" }}>
            Thank you! We'll review your request and contact you within 24–48 hours with pricing and availability.
          </p>
          <button onClick={() => setSuccess(false)}
            style={{ padding: "11px 28px", borderRadius: "10px", background: "#e07b39",
              color: "#fff", border: "none", cursor: "pointer", fontFamily: "Georgia, serif", fontSize: "13px", fontWeight: "700" }}>
            Submit Another
          </button>
        </div>
      </section>
    );
  }

  return (
    <section id="customize" style={{ background: "linear-gradient(160deg, #1a1a1a 0%, #2d1f0e 100%)", padding: "80px 6%" }}>
      <div style={{ maxWidth: "860px", margin: "0 auto" }}>

        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: "52px" }}>
          <p style={{ fontFamily: "Georgia, serif", color: "#e07b39", fontSize: "11px",
            letterSpacing: "0.25em", textTransform: "uppercase", marginBottom: "12px" }}>
            Made Just For You
          </p>
          <h2 style={{ fontFamily: "Georgia, serif", fontSize: "clamp(26px,4vw,40px)",
            fontWeight: "700", color: "#fff", marginBottom: "14px", lineHeight: "1.2" }}>
            Customise Your Candle
          </h2>
          <p style={{ fontFamily: "Georgia, serif", fontSize: "14px", color: "#aaa",
            maxWidth: "480px", margin: "0 auto", lineHeight: "1.8" }}>
            Choose your scent, size, and personal message. We'll handcraft it and deliver to your door.
          </p>
        </div>

        <div style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: "20px", padding: "40px 36px" }}>

          {apiError && (
            <div style={{ background: "rgba(192,57,43,0.2)", border: "1px solid #c0392b",
              borderRadius: "10px", padding: "10px 16px", marginBottom: "24px",
              fontFamily: "Georgia, serif", fontSize: "12px", color: "#e57373" }}>
              ⚠ {apiError}
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: "32px" }}>

            {/* ── Step 1: Your Details ── */}
            <div>
              <p style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: "#e07b39",
                fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: "16px" }}>
                Step 1 — Your Details
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "14px" }} className="cust-3col">
                <div>
                  <label style={label}>Full Name *</label>
                  <input value={form.full_name} onChange={set("full_name")} placeholder="Your name" style={inputStyle("full_name")} />
                  {errMsg("full_name")}
                </div>
                <div>
                  <label style={label}>Email *</label>
                  <input value={form.email} onChange={set("email")} type="email" placeholder="your@email.com" style={inputStyle("email")} />
                  {errMsg("email")}
                </div>
                <div>
                  <label style={label}>Phone Number *</label>
                  <input value={form.phone} onChange={set("phone")} placeholder="10-digit number" style={inputStyle("phone")} />
                  {errMsg("phone")}
                </div>
              </div>
            </div>

            {/* ── Step 2: Delivery Address ── */}
            <div>
              <p style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: "#e07b39",
                fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: "8px" }}>
                Step 2 — Delivery Address
              </p>
              <p style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#666", marginBottom: "12px" }}>
                Auto-filled from your profile. Edit if needed.
              </p>
              <textarea value={form.address} onChange={set("address")} rows={3}
                placeholder="House/Flat no, Street, Landmark, City, State — PIN, Country"
                style={{ ...inputStyle("address"), resize: "vertical" }} />
            </div>

            {/* ── Step 3: Choose Scent ── */}
            <div>
              <p style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: "#e07b39",
                fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: "16px" }}>
                Step 3 — Choose Scent *
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: "10px" }} className="cust-4col">
                {SCENTS.map(s => (
                  <button key={s.value}
                    onClick={() => { setForm(f => ({ ...f, scent: s.value })); setErrors(e => ({ ...e, scent: "" })); }}
                    style={{ padding: "14px 8px", borderRadius: "12px", cursor: "pointer",
                      fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: "600", textAlign: "center",
                      border: `1.5px solid ${form.scent === s.value ? "#e07b39" : "rgba(255,255,255,0.12)"}`,
                      background: form.scent === s.value ? "rgba(224,123,57,0.25)" : "rgba(255,255,255,0.04)",
                      color: form.scent === s.value ? "#e07b39" : "#ccc", transition: "all 0.2s" }}>
                    <div style={{ fontSize: "20px", marginBottom: "5px" }}>{s.emoji}</div>
                    {s.label}
                  </button>
                ))}
              </div>
              {errMsg("scent")}
            </div>

            {/* ── Step 4: Choose Size ── */}
            <div>
              <p style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: "#e07b39",
                fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: "16px" }}>
                Step 4 — Choose Size *
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: "12px" }} className="cust-4col">
                {SIZES.map(sz => (
                  <button key={sz.value}
                    onClick={() => { setForm(f => ({ ...f, size: sz.value })); setErrors(e => ({ ...e, size: "" })); }}
                    style={{ padding: "18px 12px", borderRadius: "14px", cursor: "pointer",
                      fontFamily: "Georgia, serif", textAlign: "center",
                      border: `1.5px solid ${form.size === sz.value ? "#e07b39" : "rgba(255,255,255,0.12)"}`,
                      background: form.size === sz.value ? "rgba(224,123,57,0.25)" : "rgba(255,255,255,0.04)",
                      color: form.size === sz.value ? "#e07b39" : "#ccc", transition: "all 0.2s" }}>
                    <div style={{ fontSize: "22px", marginBottom: "6px" }}>{sz.emoji}</div>
                    <div style={{ fontSize: "14px", fontWeight: "700" }}>{sz.label}</div>
                    <div style={{ fontSize: "11px", opacity: 0.8, marginTop: "3px", lineHeight: "1.4" }}>{sz.sub}</div>
                  </button>
                ))}
              </div>
              {errMsg("size")}
            </div>

            {/* ── Step 5: Personalise ── */}
            <div>
              <p style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: "#e07b39",
                fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: "16px" }}>
                Step 5 — Personalise
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }} className="cust-2col">
                <div>
                  <label style={label}>Candle Color</label>
                  <input value={form.color} onChange={set("color")} placeholder="e.g. Ivory, Blush Pink..." style={inputStyle("color")} />
                </div>
                <div>
                  <label style={label}>Quantity *</label>
                  <input value={form.quantity} onChange={set("quantity")} type="number" min="1" max="100" style={inputStyle("quantity")} />
                  {errMsg("quantity")}
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={label}>Label / Message Text</label>
                  <input value={form.label_text} onChange={set("label_text")}
                    placeholder="e.g. Happy Anniversary, With Love from Priya..." style={inputStyle("label_text")} />
                  <p style={{ fontFamily: "Georgia, serif", fontSize: "10px", color: "#666", marginTop: "4px" }}>
                    This text will be printed on your custom label.
                  </p>
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={label}>Additional Notes</label>
                  <textarea value={form.notes} onChange={set("notes")} rows={3}
                    placeholder="Any other requirements, packaging preferences..."
                    style={{ ...inputStyle("notes"), resize: "none" }} />
                </div>
              </div>
            </div>

            {/* ── Step 6: Reference Image ── */}
            <div>
              <p style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: "#e07b39",
                fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: "8px" }}>
                Step 6 — Reference Image{" "}
                <span style={{ color: "#666", fontWeight: "400", textTransform: "none", letterSpacing: 0 }}>(optional)</span>
              </p>
              <p style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: "#aaa", marginBottom: "12px", lineHeight: "1.6" }}>
                Upload a photo showing your colour, shape, or packaging inspiration.
              </p>
              <label style={{ display: "block", cursor: "pointer" }}>
                <div style={{
                  border: `2px dashed ${refPreview ? "#e07b39" : "rgba(255,255,255,0.2)"}`,
                  borderRadius: "14px", padding: "24px", textAlign: "center",
                  background: refPreview ? "rgba(224,123,57,0.08)" : "rgba(255,255,255,0.03)",
                  transition: "all 0.2s",
                }}>
                  {refPreview ? (
                    <div>
                      <img src={refPreview} alt="Preview"
                        style={{ maxHeight: "180px", maxWidth: "100%", objectFit: "contain", borderRadius: "10px", marginBottom: "10px" }} />
                      <p style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#e07b39" }}>
                        ✅ {refImage?.name} — click to change
                      </p>
                    </div>
                  ) : (
                    <>
                      <div style={{ fontSize: "32px", marginBottom: "8px" }}>🖼️</div>
                      <p style={{ fontFamily: "Georgia, serif", fontSize: "13px", color: "#ccc", marginBottom: "4px" }}>
                        Click to upload a reference image
                      </p>
                      <p style={{ fontFamily: "Georgia, serif", fontSize: "10px", color: "#666" }}>
                        JPG, PNG, WEBP · max 5 MB
                      </p>
                    </>
                  )}
                </div>
                <input type="file" accept="image/*" style={{ display: "none" }} onChange={handleImageChange} />
              </label>
              {refPreview && (
                <button onClick={() => { setRefImage(null); setRefPreview(null); }}
                  style={{ marginTop: "8px", background: "none", border: "none", cursor: "pointer",
                    fontFamily: "Georgia, serif", fontSize: "11px", color: "#888" }}>
                  ✕ Remove image
                </button>
              )}
            </div>

            {/* ── Payment Method (display only) ── */}
            <div style={{ background: "rgba(255,255,255,0.05)", borderRadius: "12px", padding: "16px 20px",
              border: "1px solid rgba(255,255,255,0.1)", display: "flex", alignItems: "center", gap: "12px" }}>
              <span style={{ fontSize: "22px" }}>💵</span>
              <div>
                <p style={{ fontFamily: "Georgia, serif", fontSize: "10px", color: "#aaa",
                  textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "3px" }}>Payment Method</p>
                <p style={{ fontFamily: "Georgia, serif", fontSize: "14px", fontWeight: "700", color: "#fff" }}>
                  Cash on Delivery (COD)
                </p>
                <p style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#666", marginTop: "2px" }}>
                  Payment collected at the time of delivery.
                </p>
              </div>
            </div>

            {/* Submit */}
            <button onClick={handleSubmit} disabled={submitting}
              style={{ width: "100%", padding: "16px", borderRadius: "12px",
                background: submitting ? "#c47a30" : "linear-gradient(135deg, #e07b39, #c96a18)",
                color: "#fff", border: "none", cursor: submitting ? "not-allowed" : "pointer",
                fontFamily: "Georgia, serif", fontSize: "15px", fontWeight: "700",
                letterSpacing: "0.06em", boxShadow: "0 6px 24px rgba(201,106,24,0.4)", transition: "all 0.2s" }}>
              {submitting ? "Submitting..." : "🕯️  Submit Customization Request"}
            </button>

            <p style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#666",
              textAlign: "center", lineHeight: "1.7" }}>
              We'll review your request and contact you within{" "}
              <strong style={{ color: "#e07b39" }}>24–48 hours</strong>{" "}
              with pricing and availability.
            </p>

          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 640px) {
          .cust-3col { grid-template-columns: 1fr !important; }
          .cust-2col { grid-template-columns: 1fr !important; }
          .cust-4col { grid-template-columns: repeat(2,1fr) !important; }
        }
      `}</style>
    </section>
  );
}
